import React, { useEffect, useState, useRef } from 'react';
function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, {
      threshold
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return {
    ref,
    inView
  };
}
export function MethodSection() {
  const {
    ref,
    inView
  } = useInView();
  const pillars = [{
    number: '01',
    icon: '📦',
    title: 'EMPAQUETA TU VALOR',
    desc: 'Aprenderás a convertir tus décadas de oficina en servicios concretos que se pagan a 30€/hora o más. Tu experiencia tiene precio. Aprende a ponerlo.'
  }, {
    number: '02',
    icon: '🎯',
    title: 'ENCUENTRA TUS CLIENTES',
    desc: 'Sabrás exactamente dónde se esconden estos clientes. Pista: no es en los portales de empleo. Son comunidades, plataformas y redes que nadie te ha enseñado.'
  }, {
    number: '03',
    icon: '💬',
    title: 'DOMINA LA NARRATIVA',
    desc: '"Yo aporto la madurez que tu negocio necesita para escalar sin romperse." Tu edad será tu mayor argumento de venta. Aprende a usarlo.'
  }];
  return <section id="method" className="py-20 lg:py-28 relative overflow-hidden" style={{
    backgroundColor: '#0A1929'
  }} ref={ref} data-id="element-115">
      {/* Background phoenix watermark */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-3 pointer-events-none select-none" style={{
      fontSize: '40rem',
      lineHeight: 1,
      color: '#C9A84C',
      opacity: 0.02
    }} data-id="element-116">
        🦅
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6" data-id="element-117">
        {/* Phoenix icon */}
        <div className="flex justify-center mb-8 transition-all duration-700" style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(20px)'
      }} data-id="element-118">
          <PhoenixLarge data-id="element-119" />
        </div>

        {/* Header */}
        <div className="text-center mb-6 transition-all duration-700 delay-100" style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)'
      }} data-id="element-120">
          <h2 className="font-heading font-black gold-shimmer mb-3" style={{
          fontSize: 'clamp(2rem, 5vw, 3.5rem)'
        }} data-id="element-121">
            El Método Fénix 45+
          </h2>
          <p className="font-heading italic text-xl" style={{
          color: '#FAF7F2'
        }} data-id="element-122">
            La reinvención profesional que no depende de algoritmos de Infojobs
          </p>
        </div>

        {/* Description */}
        <div className="max-w-2xl mx-auto text-center mb-16 transition-all duration-700 delay-200" style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(20px)'
      }} data-id="element-123">
          <p className="font-body text-lg leading-relaxed" style={{
          color: '#A8C0D8'
        }} data-id="element-124">
            He diseñado una hoja de ruta para que dejes de pedir limosna laboral
            y empieces a ofrecer soluciones de alto valor.
          </p>
          <p className="font-heading italic text-lg mt-3" style={{
          color: '#C9A84C'
        }} data-id="element-125">
            No es un curso de informática. Es un sistema de transferencia de
            poder.
          </p>
        </div>

        {/* Gold divider */}
        <div className="gold-divider mb-16" data-id="element-126" />

        {/* Three pillars */}
        <div className="grid md:grid-cols-3 gap-6" data-id="element-127">
          {pillars.map((pillar, i) => <div key={pillar.number} className="p-8 rounded-2xl transition-all duration-700" style={{
          backgroundColor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(201,168,76,0.35)',
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(40px)',
          transitionDelay: `${400 + i * 150}ms`
        }} data-id="element-128">
              <div className="font-heading font-black text-4xl mb-2" style={{
            color: 'rgba(201,168,76,0.3)'
          }} data-id="element-129">
                {pillar.number}
              </div>
              <div className="text-3xl mb-4" data-id="element-130">{pillar.icon}</div>
              <h3 className="font-body font-black text-sm tracking-widest mb-3" style={{
            color: '#C9A84C'
          }} data-id="element-131">
                {pillar.title}
              </h3>
              <p className="font-body text-sm leading-relaxed" style={{
            color: '#A8C0D8'
          }} data-id="element-132">
                {pillar.desc}
              </p>
            </div>)}
        </div>

        {/* Product image */}
        <div className="mt-16 flex justify-center transition-all duration-700 delay-700" style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)'
      }} data-id="element-133">
          <div className="relative max-w-md w-full" data-id="element-134">
            <div className="absolute inset-0 rounded-2xl blur-3xl opacity-30" style={{
            backgroundColor: '#C9A84C'
          }} data-id="element-135" />
            <img src="https://cdn.magicpatterns.com/uploads/7UFTU8fCrbNzfGX96cBi47/metodo_fenix.png" alt="El Método Fénix 45+" className="relative rounded-2xl w-full shadow-2xl" style={{
            border: '2px solid rgba(201,168,76,0.5)'
          }} data-id="element-136" />
          </div>
        </div>
      </div>
    </section>;
}
function PhoenixLarge() {
  return <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-137">
      <path d="M40 8C40 8 24 20 24 36C24 44.8 31.2 52 40 52C48.8 52 56 44.8 56 36C56 20 40 8 40 8Z" fill="#C9A84C" opacity="0.9" data-id="element-138" />
      <path d="M16 40C16 40 8 32 12 24C16 16 28 20 28 20C28 20 20 28 24 36C26.4 41.6 32 44 32 44L16 40Z" fill="#C9A84C" opacity="0.7" data-id="element-139" />
      <path d="M64 40C64 40 72 32 68 24C64 16 52 20 52 20C52 20 60 28 56 36C53.6 41.6 48 44 48 44L64 40Z" fill="#C9A84C" opacity="0.7" data-id="element-140" />
      <path d="M40 52C40 52 32 60 36 68C38 72 40 72 40 72C40 72 42 72 44 68C48 60 40 52 40 52Z" fill="#C9A84C" opacity="0.8" data-id="element-141" />
      <circle cx="40" cy="36" r="6" fill="#FAF7F2" opacity="0.9" data-id="element-142" />
      <circle cx="40" cy="36" r="3" fill="#C9A84C" data-id="element-143" />
    </svg>;
}